import { useMemo, useRef } from "react";
import PropTypes from "prop-types";

import ContentTeaser from "@/components/ContentTeaser";
import ContentVeil from "@/components/ContentVeil";
import useLangstonMediaGet from "@/content/useLangstonMediaGet";
import useHasFeature from "@/features/useHasFeature";
import uiTypes from "@/view/constants/uiTypes";
import ContentDisplayCopyright from "@/view/ContentDisplay/ContentDisplayCopyright";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import { TiptapArticleBody } from "@/view/TiptapArticleBody/TiptapArticleBody";
import getLanguageCode from "@/view/utils/getLanguageCode";
import useAccessibilityControlsContext from "@/view/utils/useAccessibilityControlsContext";
import useAnnotationsState from "@/view/utils/useAnnotationsState";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";

import DecodableContentProviderLogo from "./DecodableContentProviderLogo";
import { bodyCss, rootCss, tiptapCss } from "./ArticleBody.style";

const propTypes = {
  currentFontSize: PropTypes.string,
  isVocabularySupportEnabled: PropTypes.bool,
  keyTermDataContentId: PropTypes.string,
  language: PropTypes.string,
  legacyArticleId: PropTypes.number,
  levelId: PropTypes.string,
  rawText: PropTypes.object,
  toggleVocabularySupport: PropTypes.func,
  userProperties: PropTypes.object,
  componentOrigin: PropTypes.string,
  content: PropTypes.object,
  contentMetadata: PropTypes.object,
  lexile: PropTypes.string,
};

/**
 * The ArticleBody component displays the main text elements for an article.
 */
const ArticleBody = ({
  isVocabularySupportEnabled,
  keyTermDataContentId,
  language,
  legacyArticleId,
  levelId,
  rawText,
  toggleVocabularySupport,
  userProperties,
  componentOrigin,
  content,
  contentMetadata,
  lexile,
}) => {
  const articleBodyRef = useRef();
  const copyrightText = content?.data?.content_provider?.copyright || "";
  const contentId = content?.id || "";

  const {
    contentDataWithAnnotations,
    annotationsIncludingPossibleDraftAnnotation,
  } = useAnnotationsState();
  const { showParagraphCount, currentFontSize } =
    useAccessibilityControlsContext();
  const media = useLangstonMediaGet(contentId);
  const [hasLiteUsersToSeeMoreTextFeature] = useHasFeature(
    "lite_users_to_see_more_text",
  );
  const { uiType } = useUserContentUIContext();
  const { isReadAloudModeActive } = useReadAloudMode();

  // This component is memoized to prevent re-renders that cause issues with focus and text selection.
  return useMemo(() => {
    if (!rawText) return;
    const liteUserPremiumContent = uiType === uiTypes.LITE_USER_PREMIUM_CONTENT;

    const renderBody = () => {
      return (
        <div
          css={tiptapCss(currentFontSize, showParagraphCount)}
          data-testid="tiptap-wrapper"
        >
          <TiptapArticleBody
            currentFontSize={currentFontSize}
            componentOrigin={componentOrigin}
            keyTermDataContentId={keyTermDataContentId}
            media={media}
            levelId={levelId}
            rawText={rawText}
            toggleVocabularySupport={toggleVocabularySupport}
            userProperties={userProperties}
            legacyArticleId={legacyArticleId}
            content={content}
            language={language}
            lexile={lexile}
          />
          <DecodableContentProviderLogo
            content={content}
            contentMetadata={contentMetadata}
          />
        </div>
      );
    };

    const renderLiteBody = () => {
      return hasLiteUsersToSeeMoreTextFeature ? (
        <ContentTeaser isActive>{renderBody()}</ContentTeaser>
      ) : (
        <ContentVeil />
      );
    };

    return (
      <div css={rootCss} ref={articleBodyRef}>
        <div
          css={bodyCss(showParagraphCount && !isReadAloudModeActive)}
          lang={getLanguageCode(language)}
        >
          {liteUserPremiumContent ? renderLiteBody() : renderBody()}
        </div>
        <ContentDisplayCopyright copyright={copyrightText} />
      </div>
    );
  }, [
    showParagraphCount,
    isReadAloudModeActive,
    legacyArticleId,
    isVocabularySupportEnabled,
    JSON.stringify(annotationsIncludingPossibleDraftAnnotation),
    JSON.stringify(media),
    JSON.stringify(contentDataWithAnnotations),
    JSON.stringify(rawText),
    currentFontSize,
  ]);
};

ArticleBody.propTypes = propTypes;

export default ArticleBody;
